import React, { useState, useEffect, useCallback, useRef } from 'react';
import UploadOneDrive from '../components/UploadOneDrive';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../api';

function clienteNome(c) {
  return c?.denominazione || c?.ragione_sociale || [c?.nome, c?.cognome].filter(Boolean).join(' ') || '—';
}

const STATI_CTRL = {
  OK:   { label: 'OK',  bg: '#D1FAE5', color: '#065F46', border: '#34D399' },
  NO:   { label: 'NO',  bg: '#FEE2E2', color: '#991B1B', border: '#F87171' },
  '!':  { label: '!',   bg: '#FEF9C3', color: '#92400E', border: '#FCD34D' },
  null: { label: '—',   bg: '#F3F4F6', color: '#6B7280', border: '#D1D5DB' },
};

// ─── RICH TEXT EDITOR ────────────────────────────────────────────────────────
const COLORS = ['#000000','#374151','#DC2626','#D97706','#059669','#2563EB','#7C3AED','#DB2777'];
const HIGHLIGHTS = ['transparent','#FEF08A','#BBF7D0','#BFDBFE','#FED7AA','#FECACA','#E9D5FF','#D1FAE5'];

function RichEditor({ value, onChange }) {
  const editorRef = useRef(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showHighlight, setShowHighlight] = useState(false);
  const [showTableModal, setShowTableModal] = useState(false);
  const [showCalcModal, setShowCalcModal] = useState(false);
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);
  const [calcRows, setCalcRows] = useState(3);
  const [calcCols, setCalcCols] = useState(3);

  // init content once
  useEffect(() => {
    if (editorRef.current && value !== undefined && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || '';
    }
  }, []); // eslint-disable-line

  const exec = (cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    save();
  };

  const save = () => {
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  };

  const insertImage = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        editorRef.current?.focus();
        document.execCommand('insertHTML', false,
          `<img src="${ev.target.result}" style="max-width:100%;border-radius:4px;margin:4px 0;" />`);
        save();
      };
      reader.readAsDataURL(file);
    };
    input.click();
  };

  const insertTable = (rows, cols, isCalc) => {
    editorRef.current?.focus();
    let html = `<table data-calc="${isCalc}" style="border-collapse:collapse;width:100%;margin:8px 0;font-size:13px;table-layout:fixed;">`;
    // intestazione
    html += '<thead><tr>';
    for (let c = 0; c < cols; c++) {
      const isLast = isCalc && c === cols - 1;
      html += `<th style="border:1px solid #D1D5DB;padding:6px 10px;background:${isLast ? '#DBEAFE' : '#F3F4F6'};font-weight:700;text-align:left;user-select:text;" contenteditable="true">${isLast ? 'Totale' : 'Colonna ' + (c + 1)}</th>`;
    }
    html += '</tr></thead><tbody>';
    // righe dati
    for (let r = 0; r < rows - 1; r++) {
      html += '<tr>';
      for (let c = 0; c < cols; c++) {
        const isLast = isCalc && c === cols - 1;
        const style = `border:1px solid #D1D5DB;padding:6px 10px;text-align:${isLast ? 'right' : 'left'};background:${isLast ? '#EFF6FF' : 'white'};font-weight:${isLast ? '600' : '400'};`;
        if (isCalc && isLast) {
          // cella totale non editabile con data attribute per somma automatica
          html += `<td data-sum="true" style="${style}font-style:italic;color:#6B7280;">auto</td>`;
        } else {
          html += `<td style="${style}" contenteditable="true" oninput="(function(td){var tr=td.parentNode;var cells=tr.querySelectorAll('td:not([data-sum])');var tot=0;cells.forEach(function(c){var v=parseFloat(c.innerText.replace(',','.'));if(!isNaN(v))tot+=v;});var s=tr.querySelector('[data-sum]');if(s)s.innerText=tot.toLocaleString('it-IT',{minimumFractionDigits:2});})(this)"></td>`;
        }
      }
      html += '</tr>';
    }
    html += '</tbody></table><p><br></p>';
    document.execCommand('insertHTML', false, html);
    save();
  };

  const btnStyle = (active) => ({
    padding: '4px 8px', fontSize: 13, border: '1px solid #D1D5DB',
    borderRadius: 4, cursor: 'pointer', background: active ? '#DBEAFE' : 'white',
    color: active ? '#1D4ED8' : '#374151', fontWeight: active ? 700 : 400,
    minWidth: 28, textAlign: 'center', lineHeight: '1.4',
  });

  const sepStyle = { width: 1, background: '#E5E7EB', margin: '0 2px', alignSelf: 'stretch' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 3, padding: '6px 8px',
        borderBottom: '1px solid #E5E7EB', background: '#F9FAFB', flexShrink: 0,
      }}>
        <button style={btnStyle()} onMouseDown={e => { e.preventDefault(); exec('bold'); }} title="Grassetto"><b>B</b></button>
        <button style={btnStyle()} onMouseDown={e => { e.preventDefault(); exec('italic'); }} title="Corsivo"><i>I</i></button>
        <button style={btnStyle()} onMouseDown={e => { e.preventDefault(); exec('underline'); }} title="Sottolineato"><u>U</u></button>
        <button style={btnStyle()} onMouseDown={e => { e.preventDefault(); exec('strikeThrough'); }} title="Barrato"><s>S</s></button>

        <div style={sepStyle} />

        {/* Colore testo */}
        <div style={{ position: 'relative' }}>
          <button style={{ ...btnStyle(), paddingLeft: 6, paddingRight: 6 }}
            onMouseDown={e => { e.preventDefault(); setShowColorPicker(v => !v); setShowHighlight(false); }}
            title="Colore testo">
            <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <span style={{ fontWeight: 700, fontSize: 13 }}>A</span>
              <span style={{ width: 16, height: 3, background: '#DC2626', borderRadius: 1 }} />
            </span>
          </button>
          {showColorPicker && (
            <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 100, background: 'white', border: '1px solid #D1D5DB', borderRadius: 6, padding: 6, display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
              {COLORS.map(col => (
                <button key={col} onMouseDown={e => { e.preventDefault(); exec('foreColor', col); setShowColorPicker(false); }}
                  style={{ width: 20, height: 20, background: col, border: '1px solid #D1D5DB', borderRadius: 3, cursor: 'pointer' }} />
              ))}
            </div>
          )}
        </div>

        {/* Evidenziatore */}
        <div style={{ position: 'relative' }}>
          <button style={{ ...btnStyle(), paddingLeft: 6, paddingRight: 6 }}
            onMouseDown={e => { e.preventDefault(); setShowHighlight(v => !v); setShowColorPicker(false); }}
            title="Evidenzia testo">
            <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <span style={{ fontSize: 12 }}>🖊</span>
              <span style={{ width: 16, height: 3, background: '#FEF08A', borderRadius: 1 }} />
            </span>
          </button>
          {showHighlight && (
            <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 100, background: 'white', border: '1px solid #D1D5DB', borderRadius: 6, padding: 6, display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
              {HIGHLIGHTS.map(col => (
                <button key={col} onMouseDown={e => { e.preventDefault(); exec('hiliteColor', col); setShowHighlight(false); }}
                  style={{ width: 20, height: 20, background: col === 'transparent' ? 'white' : col, border: '1px solid #D1D5DB', borderRadius: 3, cursor: 'pointer', fontSize: col === 'transparent' ? 10 : 'inherit' }}>
                  {col === 'transparent' ? '✕' : ''}
                </button>
              ))}
            </div>
          )}
        </div>

        <div style={sepStyle} />

        <button style={btnStyle()} onMouseDown={e => { e.preventDefault(); exec('insertUnorderedList'); }} title="Elenco">≡</button>
        <button style={btnStyle()} onMouseDown={e => { e.preventDefault(); exec('insertOrderedList'); }} title="Elenco numerato">1.</button>

        <div style={sepStyle} />

        <button style={btnStyle()} onMouseDown={e => { e.preventDefault(); insertImage(); }} title="Immagine">🖼</button>

        {/* Tabella */}
        <div style={{ position: 'relative' }}>
          <button style={btnStyle()} onMouseDown={e => { e.preventDefault(); setShowTableModal(v => !v); setShowCalcModal(false); }} title="Inserisci tabella">⊞</button>
          {showTableModal && (
            <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 100, background: 'white', border: '1px solid #D1D5DB', borderRadius: 8, padding: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', minWidth: 180 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Inserisci tabella</div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <div>
                  <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 2 }}>Righe</div>
                  <input type="number" value={tableRows} min={1} max={20} onChange={e => setTableRows(+e.target.value)}
                    style={{ width: 60, padding: '4px 6px', fontSize: 13, border: '1px solid #D1D5DB', borderRadius: 4 }} />
                </div>
                <div>
                  <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 2 }}>Colonne</div>
                  <input type="number" value={tableCols} min={1} max={10} onChange={e => setTableCols(+e.target.value)}
                    style={{ width: 60, padding: '4px 6px', fontSize: 13, border: '1px solid #D1D5DB', borderRadius: 4 }} />
                </div>
              </div>
              <button onMouseDown={e => { e.preventDefault(); insertTable(tableRows, tableCols, false); setShowTableModal(false); }}
                style={{ width: '100%', padding: '6px', fontSize: 13, background: '#1D5FAA', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                Inserisci
              </button>
            </div>
          )}
        </div>

        {/* Tabella calcolata */}
        <div style={{ position: 'relative' }}>
          <button style={{ ...btnStyle(), fontSize: 11 }} onMouseDown={e => { e.preventDefault(); setShowCalcModal(v => !v); setShowTableModal(false); }} title="Tabella calcolata">∑⊞</button>
          {showCalcModal && (
            <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 100, background: 'white', border: '1px solid #D1D5DB', borderRadius: 8, padding: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', minWidth: 200 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Tabella calcolata</div>
              <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 8 }}>Ultima colonna = totale riga</div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <div>
                  <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 2 }}>Righe dati</div>
                  <input type="number" value={calcRows} min={1} max={20} onChange={e => setCalcRows(+e.target.value)}
                    style={{ width: 60, padding: '4px 6px', fontSize: 13, border: '1px solid #D1D5DB', borderRadius: 4 }} />
                </div>
                <div>
                  <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 2 }}>Colonne</div>
                  <input type="number" value={calcCols} min={2} max={10} onChange={e => setCalcCols(+e.target.value)}
                    style={{ width: 60, padding: '4px 6px', fontSize: 13, border: '1px solid #D1D5DB', borderRadius: 4 }} />
                </div>
              </div>
              <button onMouseDown={e => { e.preventDefault(); insertTable(calcRows + 1, calcCols, true); setShowCalcModal(false); }}
                style={{ width: '100%', padding: '6px', fontSize: 13, background: '#059669', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                Inserisci
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Editing area */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={save}
        onBlur={save}
        onClick={() => { setShowColorPicker(false); setShowHighlight(false); setShowTableModal(false); setShowCalcModal(false); }}
        style={{
          flex: 1, overflowY: 'auto', padding: '12px 14px',
          fontSize: 14, lineHeight: 1.6, color: '#111827',
          outline: 'none', minHeight: 0,
        }}
        data-placeholder="Scrivi le note generali del bilancio…"
      />

      <style>{`
        [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #9CA3AF;
          font-style: italic;
          pointer-events: none;
        }
        [contenteditable] table td, [contenteditable] table th {
          border: 1px solid #D1D5DB !important;
          padding: 5px 8px;
          min-width: 60px;
        }
        [contenteditable] table td:focus, [contenteditable] table th:focus {
          outline: 2px solid #93C5FD;
          outline-offset: -1px;
        }
      `}</style>
    </div>
  );
}

// ─── SIDEBAR DESTRA NOTE ──────────────────────────────────────────────────────
function NoteSidebar({ det, onSave }) {
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('noteSidebarCollapsed') === 'true');
  const [content, setContent] = useState(det?.note_generali || '');
  const [saving, setSaving] = useState(false);
  const saveTimer = useRef(null);

  useEffect(() => {
    setContent(det?.note_generali || '');
  }, [det?.id]); // eslint-disable-line

  const handleChange = (html) => {
    setContent(html);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      await onSave(html);
      setSaving(false);
    }, 800);
  };

  const toggle = () => setCollapsed(v => {
    const next = !v;
    localStorage.setItem('noteSidebarCollapsed', String(next));
    return next;
  });

  const WIDTH = 320;

  return (
    <div style={{
      width: collapsed ? 40 : WIDTH,
      minWidth: collapsed ? 40 : WIDTH,
      transition: 'width 0.22s ease, min-width 0.22s ease',
      borderLeft: '2px solid #E5E7EB',
      background: '#FAFAFA',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      overflow: 'hidden',
      flexShrink: 0,
    }}>
      {/* Toggle button */}
      <button onClick={toggle} title={collapsed ? 'Espandi note' : 'Comprimi note'}
        style={{
          position: 'absolute', top: 12,
          left: collapsed ? '50%' : 8,
          transform: collapsed ? 'translateX(-50%)' : 'none',
          zIndex: 10, width: 26, height: 26,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'white', border: '1px solid #D1D5DB', borderRadius: 6,
          cursor: 'pointer', fontSize: 13, color: '#374151',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          transition: 'left 0.22s ease',
          flexShrink: 0,
        }}>
        {collapsed ? '❮' : '❯'}
      </button>

      {/* Collapsed label */}
      {collapsed && (
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            writingMode: 'vertical-rl', transform: 'rotate(180deg)',
            fontSize: 12, fontWeight: 600, color: '#6B7280',
            letterSpacing: '0.5px', textTransform: 'uppercase',
            marginTop: 48, userSelect: 'none',
          }}>Note bilancio</div>
        </div>
      )}

      {/* Full content */}
      {!collapsed && (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', paddingTop: 0, minHeight: 0 }}>
          {/* Header */}
          <div style={{ padding: '10px 12px 8px 42px', borderBottom: '1px solid #E5E7EB', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#374151' }}>📝 Note bilancio</div>
            {saving && <div style={{ fontSize: 11, color: '#9CA3AF', fontStyle: 'italic' }}>Salvataggio…</div>}
            {!saving && <div style={{ fontSize: 11, color: '#10B981' }}>✓ Salvato</div>}
          </div>

          {/* Editor */}
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <RichEditor value={content} onChange={handleChange} />
          </div>

          {/* Allegati dopo le note */}
          {det?.id && (
            <AllegatiNoteSection bilancioId={det.id} />
          )}
        </div>
      )}
    </div>
  );
}

// ─── ALLEGATI NELLA SIDEBAR NOTE ─────────────────────────────────────────────
function AllegatiNoteSection({ bilancioId }) {
  const [allegati, setAllegati] = React.useState([]);
  const [loading, setLoading]   = React.useState(true);
  const [desc, setDesc]         = React.useState('');
  const fileRef = React.useRef(null);

  const load = React.useCallback(() => {
    setLoading(true);
    fetch('/api/bilanci/' + bilancioId + '/allegati', {
      headers: { Authorization: 'Bearer ' + (localStorage.getItem('sp_token') || '') }
    }).then(r => r.json()).then(j => {
      if (j.success) setAllegati(j.data || []);
    }).finally(() => setLoading(false));
  }, [bilancioId]);

  React.useEffect(() => { load(); }, [load]);

  const handleUpload = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('descrizione', desc || file.name);
    const r = await fetch('/api/bilanci/' + bilancioId + '/allegati', {
      method: 'POST',
      headers: { Authorization: 'Bearer ' + (localStorage.getItem('sp_token') || '') },
      body: fd,
    });
    const j = await r.json();
    if (j.success) { setDesc(''); load(); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare allegato?')) return;
    await fetch('/api/bilanci/allegati/' + id, {
      method: 'DELETE',
      headers: { Authorization: 'Bearer ' + (localStorage.getItem('sp_token') || '') }
    });
    load();
  };

  const fileUrl = (a) => a.url || (a.filename && !a.filename.includes('.') && a.filename.length > 20
    ? 'https://drive.google.com/file/d/' + a.filename + '/view'
    : '/uploads/' + a.filename);

  const ext = (a) => (a.filename || '').split('.').pop().toLowerCase();
  const isImg = (a) => ['jpg','jpeg','png','gif','webp'].includes(ext(a));

  return (
    <div style={{ borderTop: '1px solid #E5E7EB', padding: '10px 12px', flexShrink: 0 }}>
      <div style={{ fontWeight: 700, fontSize: 12, color: '#374151', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
        📎 Allegati ({allegati.length})
      </div>

      {/* Upload */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
        <input value={desc} onChange={e => setDesc(e.target.value)}
          placeholder="Descrizione…"
          style={{ flex: 1, fontSize: 12, padding: '4px 8px', border: '1px solid #D1D5DB', borderRadius: 5 }} />
        <button onClick={() => fileRef.current?.click()}
          style={{ fontSize: 12, padding: '4px 10px', background: 'var(--accent)', color: 'white',
            border: 'none', borderRadius: 5, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          + Allega
        </button>
        <input ref={fileRef} type="file" multiple style={{ display: 'none' }}
          onChange={async e => {
            const files = Array.from(e.target.files);
            for (const f of files) await handleUpload(f);
            e.target.value='';
          }} />
      </div>

      {/* Lista allegati */}
      {loading ? <div style={{ fontSize: 11, color: '#9CA3AF' }}>⏳</div>
      : allegati.length === 0 ? <div style={{ fontSize: 11, color: '#9CA3AF', fontStyle: 'italic' }}>Nessun allegato</div>
      : allegati.map(a => (
        <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 0',
          borderBottom: '1px solid #F3F4F6' }}>
          {/* Anteprima */}
          {isImg(a) ? (
            <a href={fileUrl(a)} target="_blank" rel="noreferrer">
              <img src={fileUrl(a)} alt="" style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 3, border: '1px solid #E5E7EB' }} />
            </a>
          ) : (
            <span style={{ fontSize: 18 }}>{ext(a) === 'pdf' ? '📄' : ext(a) === 'xlsx' || ext(a) === 'xls' ? '📊' : ext(a) === 'docx' || ext(a) === 'doc' ? '📝' : '📎'}</span>
          )}
          {/* Nome + link */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <a href={fileUrl(a)} target="_blank" rel="noreferrer"
              style={{ fontSize: 12, color: '#1D5FAA', textDecoration: 'none',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
              {a.descrizione || a.filename?.split('/').pop()?.replace(/^\d+_/, '') || 'file'}
            </a>
          </div>
          <button onClick={() => handleDelete(a.id)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', fontSize: 14, flexShrink: 0 }}>×</button>
        </div>
      ))}
    </div>
  );
}

// ─── EXPORT CSV ──────────────────────────────────────────────────────────────
function exportCSV(det, controlli) {
  const rows = [
    ['Bilancio', det.anno, clienteNome(det)],
    [],
    ['ATTIVITA DI CONTROLLO', 'STATO', 'NOTE'],
    ...controlli.map(c => [c.nome, c.stato || '—', c.note || '']),
    [],
    ['Totale', controlli.length],
    ['OK', controlli.filter(c => c.stato === 'OK').length],
    ['NO', controlli.filter(c => c.stato === 'NO').length],
  ];
  const csv = rows.map(r => r.map(v => '"' + String(v).replace(/"/g, '""') + '"').join(';')).join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = 'Bilancio_' + clienteNome(det) + '_' + det.anno + '.csv'; a.click();
  URL.revokeObjectURL(url);
}

// ─── OPZIONI STATO ───────────────────────────────────────────────────────────
const OPZIONI_STATO = [
  { val: 'OK', label: 'OK', bg: '#D1FAE5', color: '#065F46', border: '#34D399' },
  { val: 'NO', label: 'NO', bg: '#FEE2E2', color: '#991B1B', border: '#F87171' },
  { val: '!',  label: '!',  bg: '#FEF9C3', color: '#92400E', border: '#FCD34D' },
  { val: null, label: '—',  bg: '#F3F4F6', color: '#6B7280', border: '#D1D5DB' },
];

// ─── RIGA CONTROLLO ──────────────────────────────────────────────────────────
function RigaCtrl({ ctrl, idx, onUpdate, onDelete, onDragStart, onDragOver, onDrop, isDragOver, selected, onSelect, filtroAttivo }) {
  const [editNote, setEditNote]   = useState(false);
  const [note, setNote]           = useState(ctrl.note || '');
  const [nome, setNome]           = useState(ctrl.nome || '');
  const [editNome, setEditNome]   = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const pickerRef = useRef(null);

  // chiudi picker cliccando fuori
  useEffect(() => {
    if (!showPicker) return;
    const handler = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) setShowPicker(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showPicker]);

  const impostaStato = async (next) => {
    await fetch('/api/bilanci/controlli/' + ctrl.id, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...ctrl, stato: next, note: ctrl.note, nome: ctrl.nome }),
    });
    onUpdate({ ...ctrl, stato: next });
    setShowPicker(false);
  };

  const handleClickStato = () => {
    if (filtroAttivo) {
      // con filtro attivo: apri sempre il picker esplicito
      setShowPicker(v => !v);
    } else {
      // senza filtro: ciclo diretto
      const next = ctrl.stato === null ? 'OK' : ctrl.stato === 'OK' ? 'NO' : ctrl.stato === 'NO' ? '!' : null;
      impostaStato(next);
    }
  };

  const salvaNote = async () => {
    await fetch('/api/bilanci/controlli/' + ctrl.id, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...ctrl, note, nome }),
    });
    onUpdate({ ...ctrl, note, nome });
    setEditNote(false); setEditNome(false);
  };

  const st = STATI_CTRL[ctrl.stato] || STATI_CTRL[null];

  return (
    <tr draggable onDragStart={() => onDragStart(idx)}
      onDragOver={e => { e.preventDefault(); onDragOver(idx); }}
      onDrop={() => onDrop(idx)}
      style={{
        borderBottom: '1px solid #E5E7EB',
        background: isDragOver ? '#DBEAFE' : selected ? '#EFF6FF' : ctrl.stato === 'OK' ? '#F0FFF4' : ctrl.stato === 'NO' ? '#FFF5F5' : ctrl.stato === '!' ? '#FEFCE8' : 'white',
        cursor: 'grab', outline: selected ? '2px solid #93C5FD' : 'none', outlineOffset: -1,
      }}>
      <td style={{ width: 28, padding: '0 4px', textAlign: 'center', color: '#D1D5DB', fontSize: 14 }}>⠿</td>
      <td style={{ width: 36, padding: '0 8px', textAlign: 'center' }} onClick={e => { e.stopPropagation(); onSelect(); }}>
        <input type="checkbox" checked={!!selected} onChange={onSelect} style={{ cursor: 'pointer', accentColor: '#1D5FAA' }} />
      </td>
      <td style={{ padding: '10px 12px', fontSize: 14, minWidth: 200 }} onDoubleClick={() => setEditNome(true)}>
        {editNome ? (
          <input value={nome} onChange={e => setNome(e.target.value)}
            onBlur={salvaNote} onKeyDown={e => e.key === 'Enter' && salvaNote()}
            autoFocus style={{ width: '100%', fontSize: 14, padding: '2px 6px', border: '1px solid #93C5FD', borderRadius: 4 }} />
        ) : (
          <span style={{ color: ctrl.stato === 'NO' ? '#991B1B' : ctrl.stato === '!' ? '#92400E' : '#111827', fontWeight: ctrl.stato === 'NO' || ctrl.stato === '!' ? 600 : 400 }}>
            {ctrl.nome}
          </span>
        )}
      </td>

      {/* ── Cella stato con popover ── */}
      <td style={{ width: 90, padding: '10px 8px', textAlign: 'center', position: 'relative' }}>
        <button
          onClick={handleClickStato}
          title={filtroAttivo ? 'Filtro attivo — clicca per scegliere il nuovo stato' : 'Clicca per cambiare stato'}
          style={{
            width: 60, padding: '5px 0', borderRadius: 6, cursor: 'pointer',
            border: '1.5px solid ' + st.border, background: st.bg,
            color: st.color, fontWeight: 700, fontSize: 13,
            // anello ambra quando filtro attivo, per segnalare che serve conferma
            outline: filtroAttivo ? '2px solid #FCD34D' : 'none',
            outlineOffset: 2,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2,
            margin: '0 auto',
          }}>
          <span>{st.label}</span>
          {filtroAttivo && <span style={{ fontSize: 9, opacity: 0.7 }}>▾</span>}
        </button>

        {/* Popover selezione stato */}
        {showPicker && (
          <div ref={pickerRef} style={{
            position: 'absolute', top: '110%', left: '50%', transform: 'translateX(-50%)',
            zIndex: 300, background: 'white',
            border: '1.5px solid #D1D5DB', borderRadius: 10, padding: 10,
            boxShadow: '0 8px 28px rgba(0,0,0,0.18)', minWidth: 170,
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6, textAlign: 'center' }}>
              Scegli nuovo stato
            </div>
            <div style={{ fontSize: 11, color: '#9CA3AF', textAlign: 'center', marginBottom: 8, fontStyle: 'italic' }}>
              Stato attuale: <b style={{ color: st.color }}>{st.label}</b>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              {OPZIONI_STATO.filter(o => String(o.val) !== String(ctrl.stato)).map(o => (
                <button key={String(o.val)} onClick={() => impostaStato(o.val)}
                  style={{
                    padding: '7px 14px', borderRadius: 6, cursor: 'pointer',
                    border: '1.5px solid ' + o.border, background: o.bg,
                    color: o.color, fontWeight: 700, fontSize: 14, textAlign: 'center',
                  }}
                  onMouseEnter={e => e.currentTarget.style.filter = 'brightness(0.93)'}
                  onMouseLeave={e => e.currentTarget.style.filter = ''}>
                  {o.label}
                </button>
              ))}
            </div>
            <button onClick={() => setShowPicker(false)}
              style={{ marginTop: 8, width: '100%', padding: '4px', fontSize: 11, background: 'none', border: '1px solid #E5E7EB', borderRadius: 5, cursor: 'pointer', color: '#6B7280' }}>
              Annulla
            </button>
          </div>
        )}
      </td>

      <td style={{ padding: '10px 12px', fontSize: 14 }} onClick={() => !editNote && setEditNote(true)}>
        {editNote ? (
          <div style={{ display: 'flex', gap: 6 }}>
            <textarea value={note} onChange={e => setNote(e.target.value)} autoFocus rows={2}
              style={{ flex: 1, fontSize: 14, padding: '4px 6px', border: '1px solid #93C5FD', borderRadius: 4, resize: 'vertical' }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <button onClick={salvaNote} style={{ padding: '4px 8px', fontSize: 12, background: '#1D5FAA', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>✓</button>
              <button onClick={() => { setEditNote(false); setNote(ctrl.note || ''); }}
                style={{ padding: '4px 8px', fontSize: 12, background: '#F3F4F6', border: '1px solid #D1D5DB', borderRadius: 4, cursor: 'pointer' }}>✕</button>
            </div>
          </div>
        ) : (
          <span style={{ color: note ? '#374151' : '#9CA3AF', fontStyle: note ? 'normal' : 'italic', display: 'block', minHeight: 22, cursor: 'text' }}>
            {note || 'Clicca per aggiungere note…'}
          </span>
        )}
      </td>
      {/* Allegati multipli per riga */}
      <AllegatiRiga ctrlId={ctrl.id} />
      <td style={{ width: 36, padding: '10px 4px', textAlign: 'center' }}>
        <button onClick={() => onDelete(ctrl.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#EF4444' }}>×</button>
      </td>
    </tr>
  );
}

// ─── ALLEGATI MULTIPLI PER RIGA CONTROLLO ────────────────────────────────────
function AllegatiRiga({ ctrlId }) {
  const [allegati, setAllegati] = useState([]);
  const [open, setOpen]         = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    fetch('/api/bilanci/controlli/' + ctrlId + '/allegati', {
      headers: { Authorization: 'Bearer ' + (localStorage.getItem('sp_token') || '') }
    }).then(r => r.json()).then(j => { if (j.success) setAllegati(j.data || []); });
  }, [ctrlId]);

  useEffect(() => { load(); }, [load]);

  const handleFiles = async (files) => {
    setUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      await fetch('/api/bilanci/controlli/' + ctrlId + '/allegati', {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + (localStorage.getItem('sp_token') || '') },
        body: fd,
      });
    }
    setUploading(false);
    load();
  };

  const handleDelete = async (allegId, nome) => {
    if (!window.confirm(`Eliminare l'allegato "${nome}"?`)) return;
    await fetch('/api/bilanci/controlli/allegati/' + allegId, {
      method: 'DELETE',
      headers: { Authorization: 'Bearer ' + (localStorage.getItem('sp_token') || '') }
    });
    load();
  };

  const ext = (a) => (a.filename || '').split('.').pop().toLowerCase();
  const isImg = (a) => ['jpg','jpeg','png','gif','webp'].includes(ext(a));
  const icon = (a) => ext(a) === 'pdf' ? '📄' : ext(a) === 'xlsx' || ext(a) === 'xls' ? '📊' : ext(a) === 'docx' || ext(a) === 'doc' ? '📝' : '📎';
  const url = (a) => a.url || '/uploads/' + a.filename;

  const n = allegati.length;

  return (
    <td style={{ width: 80, padding: '6px 6px', textAlign: 'center', verticalAlign: 'middle', position: 'relative' }}>
      {/* Badge + toggle */}
      <button onClick={() => setOpen(v => !v)}
        title={n > 0 ? `${n} allegat${n === 1 ? 'o' : 'i'} — clicca per gestire` : 'Aggiungi allegati'}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3,
          fontSize: 16, background: 'none', border: n > 0 ? '1px solid #BFDBFE' : '1px dashed #D1D5DB',
          borderRadius: 6, padding: '4px 8px', cursor: 'pointer',
          color: n > 0 ? '#1D5FAA' : '#9CA3AF', background: n > 0 ? '#EFF6FF' : 'transparent',
        }}>
        📎
        {n > 0 && <span style={{ fontSize: 11, fontWeight: 700 }}>{n}</span>}
      </button>

      {/* Pannello espanso */}
      {open && (
        <div style={{
          position: 'absolute', top: '110%', right: 0, zIndex: 400,
          background: 'white', border: '1.5px solid #D1D5DB', borderRadius: 10,
          padding: 10, boxShadow: '0 8px 28px rgba(0,0,0,0.15)', minWidth: 220, maxWidth: 280,
          textAlign: 'left',
        }}
          onClick={e => e.stopPropagation()}>
          <div style={{ fontWeight: 700, fontSize: 12, color: '#374151', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>📎 Allegati ({n})</span>
            <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', fontSize: 14 }}>✕</button>
          </div>

          {/* Lista */}
          {allegati.map(a => (
            <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0', borderBottom: '1px solid #F3F4F6' }}>
              {isImg(a)
                ? <a href={url(a)} target="_blank" rel="noreferrer"><img src={url(a)} alt="" style={{ width: 28, height: 28, objectFit: 'cover', borderRadius: 3, border: '1px solid #E5E7EB' }} /></a>
                : <span style={{ fontSize: 18 }}>{icon(a)}</span>}
              <a href={url(a)} target="_blank" rel="noreferrer"
                style={{ flex: 1, fontSize: 12, color: '#1D5FAA', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {a.descrizione || (a.filename || '').split('/').pop().replace(/^\d+_/, '') || 'file'}
              </a>
              <button onClick={() => handleDelete(a.id, a.descrizione || a.filename)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', fontSize: 14, flexShrink: 0 }}>×</button>
            </div>
          ))}
          {n === 0 && <div style={{ fontSize: 12, color: '#9CA3AF', fontStyle: 'italic', marginBottom: 8 }}>Nessun allegato</div>}

          {/* Upload */}
          <button onClick={() => fileRef.current?.click()} disabled={uploading}
            style={{ marginTop: 8, width: '100%', padding: '6px', fontSize: 12, background: uploading ? '#F9FAFB' : 'var(--accent)', color: uploading ? '#9CA3AF' : 'white', border: 'none', borderRadius: 6, cursor: uploading ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
            {uploading ? '⏳ Caricamento…' : '+ Allega file (anche multipli)'}
          </button>
          <input ref={fileRef} type="file" multiple style={{ display: 'none' }}
            onChange={async e => { await handleFiles(Array.from(e.target.files)); e.target.value = ''; }} />
        </div>
      )}
    </td>
  );
}
function ResizableHeader({ selected, filtraCtrl, toggleAll }) {
  const [colWidths, setColWidths] = React.useState({ attivita: 320, note: 280 });
  const resizing = useRef(null);

  const startResize = (col, e) => {
    e.preventDefault();
    resizing.current = { col, startX: e.clientX, startW: colWidths[col] };
    const onMove = (ev) => {
      if (!resizing.current) return;
      setColWidths(p => ({ ...p, [resizing.current.col]: Math.max(100, resizing.current.startW + ev.clientX - resizing.current.startX) }));
    };
    const onUp = () => { resizing.current = null; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const thBase = { padding: '10px 12px', textAlign: 'left', fontSize: 13, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px', position: 'relative', userSelect: 'none' };
  const handle = (col) => (
    <div onMouseDown={e => startResize(col, e)}
      style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 5, cursor: 'col-resize', zIndex: 10, background: 'transparent' }}
      onMouseEnter={e => e.currentTarget.style.background = '#93C5FD'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'} />
  );

  return (
    <thead style={{ position: 'sticky', top: 0, zIndex: 5 }}>
      <tr style={{ background: '#F3F4F6', borderBottom: '2px solid #D1D5DB' }}>
        <th style={{ width: 28 }}></th>
        <th style={{ width: 36, padding: '10px 8px', textAlign: 'center' }}>
          <input type="checkbox" checked={selected.size > 0 && selected.size === filtraCtrl.length}
            onChange={() => toggleAll(filtraCtrl)} style={{ cursor: 'pointer', accentColor: '#1D5FAA' }} />
        </th>
        <th style={{ ...thBase, width: colWidths.attivita }}>Attività di controllo{handle('attivita')}</th>
        <th style={{ padding: '10px 8px', width: 80, textAlign: 'center', fontSize: 13, fontWeight: 700, color: '#374151', textTransform: 'uppercase' }}>Stato</th>
        <th style={{ ...thBase, width: colWidths.note }}>Note{handle('note')}</th>
        <th style={{ width: 36 }}></th>
      </tr>
    </thead>
  );
}

// ─── DETTAGLIO BILANCIO ───────────────────────────────────────────────────────
export function BilancioDettaglio({ addToast }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const onClose = () => navigate('/bilanci');
  const [det, setDet] = useState(null);
  const [controlli, setControlli] = useState([]);
  const [allegati, setAllegati] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nuovoCtrl, setNuovoCtrl] = useState('');
  const [filtroStato, setFiltroStato] = useState('TUTTI');
  const [tab, setTab] = useState('controlli');
  const [dragIdx, setDragIdx] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [lastSelectedId, setLastSelectedId] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [importingXls, setImportingXls] = useState(false);
  const [descAlleg, setDescAlleg] = useState('');
  const xlsRef = useRef();
  const [cartBil, setCartBil] = useState(null);      // { collegata, esiste, percorso, messaggio }
  const [creandoCart, setCreandoCart] = useState(false);

  const tokenSp = () => 'Bearer ' + (localStorage.getItem('sp_token') || '');
  const caricaCartBil = useCallback(() => {
    fetch('/api/bilanci/' + id + '/cartella-bilancio', { headers: { Authorization: tokenSp() } })
      .then(r => r.json()).then(j => setCartBil(j.success ? j.data : null)).catch(() => setCartBil(null));
  }, [id]);
  useEffect(() => { caricaCartBil(); }, [caricaCartBil]);

  const creaCartellaBilancio = async () => {
    setCreandoCart(true);
    try {
      const r = await fetch('/api/bilanci/' + id + '/cartella-bilancio', { method: 'POST', headers: { Authorization: tokenSp() } });
      const j = await r.json();
      if (!j.success) throw new Error(j.error || 'Errore');
      addToast(j.data.creata ? 'Cartella creata su OneDrive: ' + j.data.percorso : 'La cartella esiste già: ' + j.data.percorso, 'success');
      caricaCartBil();
    } catch (e) { addToast(e.message, 'error'); }
    finally { setCreandoCart(false); }
  };

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      fetch('/api/bilanci/' + id, { headers: { Authorization: 'Bearer ' + (localStorage.getItem('sp_token')||'') } }).then(r => r.json()),
      fetch('/api/bilanci/' + id + '/allegati', { headers: { Authorization: 'Bearer ' + (localStorage.getItem('sp_token')||'') } }).then(r => r.json()),
    ]).then(([jb, ja]) => {
      if (jb.success) { setDet(jb.data); setControlli(jb.data.controlli || []); }
      if (ja.success) setAllegati(ja.data);
    }).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const toggleSelect = (sid) => setSelected(p => {
    const n = new Set(p); n.has(sid) ? n.delete(sid) : n.add(sid);
    if (!n.has(sid)) { /* deselected */ } else setLastSelectedId(sid);
    return n;
  });
  const toggleAll = (filtrati) => setSelected(p => p.size === filtrati.length ? new Set() : new Set(filtrati.map(c => c.id)));

  const cambioMassivoStato = async (nuovoStato) => {
    if (selected.size === 0) return;
    for (const ctrlId of [...selected]) {
      const ctrl = controlli.find(c => c.id === ctrlId);
      if (!ctrl) continue;
      await fetch('/api/bilanci/controlli/' + ctrlId, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...ctrl, stato: nuovoStato === 'null' ? null : nuovoStato }),
      });
    }
    setControlli(p => p.map(c => selected.has(c.id) ? { ...c, stato: nuovoStato === 'null' ? null : nuovoStato } : c));
    setSelected(new Set());
  };

  const updateCtrl = (updated) => setControlli(p => p.map(c => c.id === updated.id ? updated : c));

  const eliminaMassiva = async () => {
    if (selected.size === 0) return;
    const nomi = [...selected].map(sid => controlli.find(c => c.id === sid)?.nome || '').filter(Boolean);
    const anteprima = nomi.slice(0, 5).map(n => `• ${n}`).join('\n');
    const resto = nomi.length > 5 ? `\n… e altre ${nomi.length - 5}` : '';
    if (!window.confirm(`Eliminare ${selected.size} attività?\n\n${anteprima}${resto}\n\nL'operazione non è reversibile.`)) return;
    for (const ctrlId of [...selected]) {
      await fetch('/api/bilanci/controlli/' + ctrlId, { method: 'DELETE', headers: { Authorization: 'Bearer '+(localStorage.getItem('sp_token')||'') } });
    }
    setControlli(p => p.filter(c => !selected.has(c.id)));
    setSelected(new Set());
    setLastSelectedId(null);
  };
  const deleteCtrl = async (ctrlId) => {
    const ctrl = controlli.find(c => c.id === ctrlId);
    if (!window.confirm(`Eliminare l'attività "${ctrl?.nome || ''}"?\nL'operazione non è reversibile.`)) return;
    await fetch('/api/bilanci/controlli/' + ctrlId, { method: 'DELETE', headers: { Authorization: 'Bearer '+(localStorage.getItem('sp_token')||'') } });
    setControlli(p => p.filter(c => c.id !== ctrlId));
  };

  const aggiungiCtrl = async () => {
    if (!nuovoCtrl.trim()) return;
    const res = await fetch('/api/bilanci/' + id + '/controlli', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ nome: nuovoCtrl.trim() }) });
    const j = await res.json();
    if (j.success) {
      const nuova = j.data;
      setControlli(p => {
        // inserisci dopo l'ultima riga selezionata, o in fondo se nessuna
        const idx = lastSelectedId ? p.findIndex(c => c.id === lastSelectedId) : -1;
        const list = [...p];
        list.splice(idx >= 0 ? idx + 1 : list.length, 0, nuova);
        // se inserita in mezzo, riordina sul server
        if (idx >= 0 && idx < list.length - 1) {
          fetch('/api/bilanci/' + id + '/riordina', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ordine: list.map(c => c.id) }) });
        }
        return list;
      });
      setNuovoCtrl('');
      setLastSelectedId(nuova.id);
      setSelected(new Set([nuova.id]));
    }
  };

  const handleDrop = async (toIdx) => {
    if (dragIdx === null || dragIdx === toIdx) return;
    const newList = [...controlli];
    const [moved] = newList.splice(dragIdx, 1);
    newList.splice(toIdx, 0, moved);
    setControlli(newList); setDragIdx(null); setDragOver(null);
    await fetch('/api/bilanci/' + id + '/riordina', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ordine: newList.map(c => c.id) }) });
  };

  const importaExcel = async (file) => {
    if (!file) return;
    setImportingXls(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await fetch('/api/bilanci/' + id + '/importa-excel', { method: 'POST', headers: { Authorization: 'Bearer '+(localStorage.getItem('sp_token')||'') }, body: fd });
      const j = await res.json();
      if (!j.success) throw new Error(j.error);
      addToast('Importati ' + j.data.importati + ' controlli', 'success');
      load();
    } catch (e) { addToast(e.message, 'error'); }
    finally { setImportingXls(false); if (xlsRef.current) xlsRef.current.value = ''; }
  };

  const deleteAllegato = async (allegId) => {
    const alleg = allegati.find(a => a.id === allegId);
    if (!window.confirm(`Eliminare l'allegato "${alleg?.descrizione || ''}"?\nL'operazione non è reversibile.`)) return;
    await fetch('/api/bilanci/allegati/' + allegId, { method: 'DELETE' });
    setAllegati(p => p.filter(a => a.id !== allegId));
  };

  const [exporting, setExporting] = useState(false);

  const exportPacchetto = async () => {
    setExporting(true);
    addToast('Preparazione pacchetto in corso…', 'info');
    try {
      const res = await fetch('/api/bilanci/' + id + '/esporta-pacchetto', { headers: { Authorization: 'Bearer ' + (localStorage.getItem('sp_token')||'') } });
      if (!res.ok) throw new Error('Errore server: ' + res.status);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Bilancio_${clienteNome(det)}_${det.anno}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      addToast('Pacchetto scaricato', 'success');
    } catch (e) {
      addToast('Errore esportazione: ' + e.message, 'error');
    } finally {
      setExporting(false);
    }
  };

  const aggiornaBilancio = async (campo, valore) => {
    await fetch('/api/bilanci/' + id, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...det, [campo]: valore }) });
    setDet(p => ({ ...p, [campo]: valore }));
  };

  const salvaNoteGenerali = async (html) => {
    await fetch('/api/bilanci/' + id, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...det, note_generali: html }) });
    setDet(p => ({ ...p, note_generali: html }));
  };

  if (loading || !det) return (
    <div className="page-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
      <div style={{ color: 'var(--text-faint)', fontSize: 16 }}>⏳ Caricamento…</div>
    </div>
  );

  const filtraCtrl = controlli.filter(c => filtroStato === 'TUTTI' ? true : filtroStato === 'DA_FARE' ? (!c.stato && c.stato !== '!') : c.stato === filtroStato);
  const nOk = controlli.filter(c => c.stato === 'OK').length;
  const nNo = controlli.filter(c => c.stato === 'NO').length;
  const nTot = controlli.length;
  const perc = nTot ? Math.round(((nOk + nNo) / nTot) * 100) : 0;
  const nomeC = clienteNome(det);

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: '#FFFFFF' }}>

      {/* ── CORPO PRINCIPALE ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>

        {/* Header */}
        <div style={{ padding: '12px 18px', borderBottom: '2px solid #E5E7EB', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, background: '#F9FAFB' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 17, color: '#111827' }}>📊 Bilancio {det.anno} — {nomeC}</div>
            <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{det.codice}{det.cf ? ' · CF: ' + det.cf : ''}</div>
          </div>
          <div style={{ minWidth: 180, textAlign: 'right' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
              <div style={{ flex: 1, height: 8, background: '#E5E7EB', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ width: perc + '%', height: '100%', borderRadius: 4, background: perc === 100 ? '#10B981' : '#1D5FAA', transition: 'width 0.3s' }} />
              </div>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#1D5FAA', minWidth: 36 }}>{perc}%</span>
            </div>
            <div style={{ fontSize: 12, color: '#6B7280' }}>
              <span style={{ color: '#059669', fontWeight: 600 }}>✓ {nOk} OK</span>{' · '}
              <span style={{ color: '#DC2626', fontWeight: 600 }}>✗ {nNo} NO</span>{' · '}
              <span>— {nTot - nOk - nNo}</span>
            </div>
          </div>
          <select value={det.stato} onChange={e => aggiornaBilancio('stato', e.target.value)}
            style={{ fontSize: 13, padding: '5px 10px', border: '1px solid #D1D5DB', borderRadius: 6, cursor: 'pointer', background: 'white' }}>
            <option value="IN_CORSO">In corso</option>
            <option value="COMPLETATO">Completato</option>
            <option value="ARCHIVIATO">Archiviato</option>
          </select>
          <input ref={xlsRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={e => importaExcel(e.target.files[0])} />
          <button onClick={() => xlsRef.current.click()} disabled={importingXls}
            style={{ padding: '6px 12px', fontSize: 13, border: '1px solid #D1D5DB', borderRadius: 6, cursor: 'pointer', background: 'white' }}>
            {importingXls ? '⏳' : '📥 Import XLS'}
          </button>
          <button onClick={creaCartellaBilancio} disabled={creandoCart}
            title={cartBil && cartBil.percorso ? 'OneDrive: ' + cartBil.percorso : (cartBil && cartBil.messaggio) || 'Crea la cartella "Bilancio" nella cartella cliente dell\'anno'}
            style={{ padding: '6px 12px', fontSize: 13, border: '1px solid ' + (cartBil && cartBil.esiste ? '#10B981' : '#D1D5DB'), borderRadius: 6, cursor: creandoCart ? 'not-allowed' : 'pointer', background: 'white', color: cartBil && cartBil.esiste ? '#047857' : 'inherit', opacity: creandoCart ? 0.7 : 1 }}>
            {creandoCart ? '⏳ Creazione…' : cartBil && cartBil.esiste ? '📁 Cartella Bilancio ✓' : '📁 Crea cartella Bilancio'}
          </button>
          <button onClick={exportPacchetto} disabled={exporting}
            style={{ padding: '6px 12px', fontSize: 13, border: '1px solid #D1D5DB', borderRadius: 6, cursor: exporting ? 'not-allowed' : 'pointer', background: exporting ? '#F9FAFB' : 'white', opacity: exporting ? 0.7 : 1 }}>
            {exporting ? '⏳ Preparazione…' : '📦 Esporta pacchetto'}
          </button>
          <button onClick={onClose}
            style={{ padding: '6px 14px', fontSize: 13, background: 'white', border: '1px solid #D1D5DB', borderRadius: 6, cursor: 'pointer', color: '#374151', display: 'flex', alignItems: 'center', gap: 6 }}>
            ← Bilanci
          </button>
        </div>

        {/* Tab bar */}
        <div style={{ display: 'flex', borderBottom: '1px solid #E5E7EB', padding: '0 18px', background: '#F9FAFB', flexShrink: 0, gap: 4 }}>
          {[['controlli', '📋 Controlli'], ['allegati', '📎 Allegati' + (allegati.length > 0 ? ' (' + allegati.length + ')' : '')]].map(([t, l]) => (
            <button key={t} onClick={() => setTab(t)} style={{
              padding: '10px 16px', fontSize: 14, fontWeight: tab === t ? 700 : 400,
              border: 'none', borderBottom: tab === t ? '3px solid #1D5FAA' : '3px solid transparent',
              background: 'none', cursor: 'pointer', color: tab === t ? '#1D5FAA' : '#6B7280', marginBottom: -1,
            }}>{l}</button>
          ))}
        </div>

        {/* Tab: Controlli */}
        {tab === 'controlli' && (
          <>
            {selected.size > 0 && (
              <div style={{ padding: '8px 18px', background: '#EFF6FF', borderBottom: '1px solid #BFDBFE', display: 'flex', gap: 8, flexShrink: 0, alignItems: 'center' }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#1D4ED8' }}>{selected.size} selezionat{selected.size === 1 ? 'a' : 'e'}</span>
                <span style={{ fontSize: 13, color: '#6B7280', marginLeft: 4 }}>Cambia stato in:</span>
                {[['OK', '✓ OK', '#D1FAE5', '#065F46'], ['NO', '✗ NO', '#FEE2E2', '#991B1B'], ['!', '⚠ !', '#FEF9C3', '#92400E'], ['null', '— Nessuno', '#F3F4F6', '#6B7280']].map(([v, l, bg, col]) => (
                  <button key={v} onClick={() => cambioMassivoStato(v)} style={{ padding: '4px 14px', fontSize: 13, borderRadius: 6, cursor: 'pointer', background: bg, color: col, border: '1px solid ' + col + '44', fontWeight: 600 }}>{l}</button>
                ))}
                <div style={{ width: 1, background: '#BFDBFE', margin: '0 4px', alignSelf: 'stretch' }} />
                <button onClick={eliminaMassiva}
                  style={{ padding: '4px 14px', fontSize: 13, borderRadius: 6, cursor: 'pointer', background: '#FEE2E2', color: '#991B1B', border: '1px solid #F8717144', fontWeight: 600 }}>
                  🗑 Elimina {selected.size}
                </button>
                <button onClick={() => setSelected(new Set())} style={{ marginLeft: 'auto', padding: '4px 10px', fontSize: 12, borderRadius: 6, cursor: 'pointer', background: 'white', border: '1px solid #BFDBFE', color: '#6B7280' }}>✕ Deseleziona</button>
              </div>
            )}
            <div style={{ padding: '8px 18px', borderBottom: '1px solid #E5E7EB', display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center', background: '#FAFAFA' }}>
              <span style={{ fontSize: 13, color: '#6B7280', marginRight: 4 }}>Filtra:</span>
              {[['TUTTI', 'Tutti (' + nTot + ')'], ['OK', '✓ OK (' + nOk + ')'], ['NO', '✗ NO (' + nNo + ')'], ['!', '! Attenzione (' + controlli.filter(c => c.stato === '!').length + ')'], ['DA_FARE', '— Da fare (' + (nTot - nOk - nNo - controlli.filter(c=>c.stato==='!').length) + ')']].map(([v, l]) => (
                <button key={v} onClick={() => setFiltroStato(v)} style={{
                  padding: '4px 14px', fontSize: 13, borderRadius: 20, cursor: 'pointer',
                  border: '1px solid ' + (filtroStato === v ? '#1D5FAA' : '#E5E7EB'),
                  background: filtroStato === v ? '#1D5FAA' : 'white',
                  color: filtroStato === v ? 'white' : '#374151',
                  fontWeight: filtroStato === v ? 600 : 400,
                }}>{l}</button>
              ))}
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
                <input placeholder="Nuova attività…" value={nuovoCtrl}
                  onChange={e => setNuovoCtrl(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && aggiungiCtrl()}
                  style={{ padding: '5px 10px', fontSize: 13, border: '1px solid #D1D5DB', borderRadius: 6, outline: 'none', width: 220 }} />
                <button onClick={aggiungiCtrl} title={lastSelectedId ? 'Inserisci sotto la riga selezionata' : 'Aggiungi in fondo'}
                  style={{ padding: '5px 14px', fontSize: 13, background: '#1D5FAA', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 500, whiteSpace: 'nowrap' }}>
                  + Aggiungi{lastSelectedId ? ' ↓' : ''}
                </button>
              </div>
            </div>
            <div style={{ flex: 1, overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <ResizableHeader selected={selected} filtraCtrl={filtraCtrl} toggleAll={toggleAll} />
                <tbody>
                  {filtraCtrl.map((ctrl, idx) => (
                    <RigaCtrl key={ctrl.id} ctrl={ctrl} idx={idx}
                      onUpdate={updateCtrl} onDelete={deleteCtrl}
                      onDragStart={i => setDragIdx(i)}
                      onDragOver={i => setDragOver(i)}
                      onDrop={handleDrop}
                      isDragOver={dragOver === idx}
                      selected={selected.has(ctrl.id)}
                      onSelect={() => toggleSelect(ctrl.id)}
                      filtroAttivo={filtroStato !== 'TUTTI'} />
                  ))}
                  {filtraCtrl.length === 0 && (
                    <tr><td colSpan={5} style={{ padding: 40, textAlign: 'center', color: '#9CA3AF', fontSize: 14 }}>Nessuna attività trovata</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Tab: Allegati */}
        {tab === 'allegati' && (
          <div style={{ flex: 1, overflow: 'auto', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ padding: '14px 16px', border: '1px solid #E5E7EB', borderRadius: 8, background: '#F9FAFB', display: 'flex', gap: 10, alignItems: 'center' }}>
              <input placeholder="Descrizione allegato…" value={descAlleg} onChange={e => setDescAlleg(e.target.value)}
                style={{ flex: 1, padding: '8px 10px', fontSize: 14, border: '1px solid #D1D5DB', borderRadius: 6 }} />
              <UploadOneDrive
                endpoint={'/api/bilanci/' + id + '/allegati'}
                extraFields={{ descrizione: descAlleg }}
                onUpload={() => { load(); setDescAlleg(''); }}
                addToast={addToast}
                label="📎 Carica file"
              />
            </div>
            {allegati.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF', fontSize: 14 }}>Nessun allegato caricato.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ background: '#F3F4F6', borderBottom: '2px solid #D1D5DB' }}>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 13, fontWeight: 700, color: '#374151', textTransform: 'uppercase' }}>Descrizione</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 13, fontWeight: 700, color: '#374151', textTransform: 'uppercase' }}>File</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 13, fontWeight: 700, color: '#374151', textTransform: 'uppercase' }}>Data</th>
                    <th style={{ width: 40 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {allegati.map(a => (
                    <tr key={a.id} style={{ borderBottom: '1px solid #E5E7EB' }}>
                      <td style={{ padding: '10px 12px', fontWeight: 500 }}>{a.descrizione}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <a href={a.url || '/uploads/' + a.filename} target="_blank" rel="noreferrer" style={{ color: '#1D5FAA', fontSize: 13 }}>
                            📄 {a.descrizione || (a.filename || '').split('/').pop().replace(/^\d+_/, '') || 'file'}
                          </a>
                          {/* Anteprima inline per PDF e immagini */}
                          {(() => {
                            const url = a.url || '/uploads/' + a.filename;
                            const nome = (a.filename || '').toLowerCase();
                            const isImg = nome.match(/\.(jpg|jpeg|png|gif|webp)$/);
                            const isPdf = nome.match(/\.pdf$/) || url.includes('drive.google.com');
                            if (isImg) return (
                              <a href={url} target="_blank" rel="noreferrer">
                                <img src={url} alt="anteprima" style={{ height:32, borderRadius:4, border:'1px solid #E5E7EB', cursor:'pointer' }} />
                              </a>
                            );
                            if (isPdf) return (
                              <a href={url} target="_blank" rel="noreferrer"
                                style={{ fontSize:11, padding:'2px 8px', background:'#EFF6FF', color:'#1D5FAA', borderRadius:4, border:'1px solid #BFDBFE' }}>
                                👁 Apri
                              </a>
                            );
                            return null;
                          })()}
                        </div>
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: 12, color: '#6B7280' }}>{new Date(a.created_at).toLocaleDateString('it-IT')}</td>
                      <td style={{ padding: '10px 4px', textAlign: 'center' }}>
                        <button onClick={() => deleteAllegato(a.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#EF4444' }}>×</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

      </div>

      {/* ── SIDEBAR DESTRA NOTE ── */}
      <NoteSidebar det={det} onSave={salvaNoteGenerali} />

    </div>
  );
}

// ─── LISTA BILANCI ────────────────────────────────────────────────────────────
export default function Bilanci({ addToast }) {
  const { id } = useParams();
  if (id) return <BilancioDettaglio addToast={addToast} />;

  const [rows, setRows] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const navigate = useNavigate();
  const [form, setForm] = useState({ id_cliente: '', anno: new Date().getFullYear() });
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState('');
  const [annoFiltro, setAnnoFiltro] = useState('');
  const [statoFiltro, setStatoFiltro] = useState('TUTTI');

  useEffect(() => { api.getClienti({ limit: 300 }).then(d => setClienti(d.rows)); }, []);

  const load = useCallback(() => {
    setLoading(true);
    fetch('/api/bilanci', { headers: { Authorization: 'Bearer '+(localStorage.getItem('sp_token')||'') } }).then(r => r.json()).then(j => { if (j.success) setRows(j.data); }).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleCrea = async () => {
    if (!form.id_cliente || !form.anno) return;
    setSaving(true);
    try {
      const res = await fetch('/api/bilanci', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer '+(localStorage.getItem('sp_token')||'') }, body: JSON.stringify(form) });
      const j = await res.json();
      if (!j.success) throw new Error(j.error);
      addToast('Bilancio creato', 'success');
      setShowForm(false);
      load();
      navigate('/bilanci/' + j.data.id);
    } catch (e) { addToast(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Eliminare questo bilancio?')) return;
    await fetch('/api/bilanci/' + id, { method: 'DELETE' });
    addToast('Eliminato', 'success');
    load();
  };

  const anni = [...new Set(rows.map(r => r.anno))].sort((a, b) => b - a);
  const filtered = rows.filter(r => {
    const nome = clienteNome(r).toLowerCase();
    const matchQ = !q || nome.includes(q.toLowerCase());
    const matchAnno = !annoFiltro || r.anno === parseInt(annoFiltro);
    const matchStato = statoFiltro === 'TUTTI' || r.stato === statoFiltro;
    return matchQ && matchAnno && matchStato;
  });

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <div className="page-title">📊 Bilanci</div>
            <span style={{ padding:'2px 8px', borderRadius:10, fontSize:11, fontWeight:700,
              background:'var(--accent)22', color:'var(--accent)' }}>{rows.length}</span>
            <span style={{ padding:'2px 8px', borderRadius:10, fontSize:11, fontWeight:700,
              background:'var(--c-amber)22', color:'var(--c-amber)' }}>
              {rows.filter(r=>r.stato==='IN_CORSO').length} in corso
            </span>
            <span style={{ padding:'2px 8px', borderRadius:10, fontSize:11, fontWeight:700,
              background:'var(--c-green)22', color:'var(--c-green)' }}>
              {rows.filter(r=>r.stato==='COMPLETATO').length} completati
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select value={annoFiltro} onChange={e => setAnnoFiltro(e.target.value)} className="form-control" style={{ width: 'auto' }}>
            <option value="">Tutti gli anni</option>
            {anni.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <div className="search-box">
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5"/><path d="M11 11l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
            <input placeholder="Cerca società…" value={q} onChange={e => setQ(e.target.value)} />
          </div>
          <button className="btn btn-primary" onClick={() => setShowForm(true)}>+ Nuovo bilancio</button>
        </div>
      </div>



      {/* Tab filtro stato */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '2px solid #E5E7EB' }}>
        {[
          ['TUTTI',      'Tutti',       rows.length],
          ['IN_CORSO',   'In corso',    rows.filter(r => r.stato === 'IN_CORSO').length],
          ['COMPLETATO', 'Completati',  rows.filter(r => r.stato === 'COMPLETATO').length],
          ['ARCHIVIATO', 'Archiviati',  rows.filter(r => r.stato === 'ARCHIVIATO').length],
        ].map(([val, lbl, cnt]) => (
          <button key={val} onClick={() => setStatoFiltro(val)}
            style={{
              padding: '10px 20px', fontSize: 14, border: 'none', cursor: 'pointer',
              background: 'none', marginBottom: -2,
              borderBottom: statoFiltro === val ? '3px solid var(--accent)' : '3px solid transparent',
              color: statoFiltro === val ? 'var(--accent)' : 'var(--text-muted)',
              fontWeight: statoFiltro === val ? 700 : 400,
            }}>
            {lbl}
            <span style={{
              marginLeft: 6, fontSize: 11, fontWeight: 700, padding: '1px 7px',
              borderRadius: 10,
              background: statoFiltro === val ? 'var(--accent)' : '#E5E7EB',
              color: statoFiltro === val ? 'white' : '#6B7280',
            }}>{cnt}</span>
          </button>
        ))}
      </div>

      <div className="card">
        <div className="table-wrap">
          {loading ? <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-faint)' }}>⏳ Caricamento…</div> : (
            <table>
              <thead><tr>
                <th>Anno</th><th>Società</th><th>Controlli</th><th>Avanzamento</th><th>Stato</th><th>Azioni</th>
              </tr></thead>
              <tbody>
                {filtered.map(r => {
                  const nOk = r.n_ok || 0; const nKo = r.n_ko || 0; const nTot = r.n_controlli || 0;
                  const perc = nTot ? Math.round(((nOk + nKo) / nTot) * 100) : 0;
                  const sc = r.stato === 'COMPLETATO' ? 'pill-green' : r.stato === 'ARCHIVIATO' ? 'pill-gray' : 'pill-blue';
                  const sl = r.stato === 'COMPLETATO' ? 'Completato' : r.stato === 'ARCHIVIATO' ? 'Archiviato' : 'In corso';
                  return (
                    <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => navigate('/bilanci/' + r.id)}>
                      <td style={{ fontWeight: 700, fontSize: 15 }}>{r.anno}</td>
                      <td className="fw-500">{clienteNome(r)}</td>
                      <td style={{ fontSize: 12 }}>
                        <span style={{ color: 'var(--c-green)' }}>✓ {nOk}</span>{' · '}
                        <span style={{ color: 'var(--c-red)' }}>✗ {nKo}</span>{' · '}
                        <span style={{ color: 'var(--text-faint)' }}>— {nTot - nOk - nKo}</span>{' / '}
                        <span>{nTot}</span>
                      </td>
                      <td style={{ minWidth: 150 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ flex: 1, height: 6, background: 'var(--bg)', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{ width: perc + '%', height: '100%', borderRadius: 3, background: perc === 100 ? 'var(--c-green)' : 'var(--accent)', transition: 'width 0.3s' }} />
                          </div>
                          <span style={{ fontSize: 11, color: 'var(--text-muted)', minWidth: 28 }}>{perc}%</span>
                        </div>
                      </td>
                      <td><span className={'pill ' + sc}>{sl}</span></td>
                      <td onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="btn btn-sm btn-primary" onClick={() => navigate('/bilanci/' + r.id)}>📋 Apri</button>
                          <button className="btn btn-sm" onClick={() => window.open('/bilanci/' + r.id, '_blank')} title="Apri in nuova scheda" style={{ padding: '4px 8px' }}>↗</button>
                          <button className="btn btn-sm btn-danger" onClick={e => handleDelete(r.id, e)}>×</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={6}><div className="empty-state"><div className="empty-state-icon">📊</div><div className="empty-state-title">Nessun bilancio trovato</div></div></td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget) setShowForm(false); }}>
          <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 24, width: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 20 }}>📊 Nuovo bilancio</div>
            <div className="form-group" style={{ marginBottom: 14 }}>
              <label className="form-label">Società *</label>
              <select className="form-control" value={form.id_cliente} onChange={e => setForm(p => ({ ...p, id_cliente: e.target.value }))}>
                <option value="">— Seleziona società —</option>
                {[...clienti].sort((a, b) => clienteNome(a).localeCompare(clienteNome(b), 'it')).map(c => (
                  <option key={c.id} value={c.id}>{clienteNome(c)} ({c.codice})</option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: 20 }}>
              <label className="form-label">Anno *</label>
              <input className="form-control" type="number" value={form.anno} min={2000} max={2099}
                onChange={e => setForm(p => ({ ...p, anno: parseInt(e.target.value) }))} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn" onClick={() => setShowForm(false)}>Annulla</button>
              <button className="btn btn-primary" onClick={handleCrea} disabled={saving || !form.id_cliente || !form.anno}>
                {saving ? 'Creo…' : '✓ Crea bilancio'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
